/**
 * cockpitTemplates — logique PURE des modèles de cockpit des rôles tenant
 * (Lot D1 du chantier « rôles dynamiques ↔ cockpits ↔ périmètres »,
 * 2026-07-16, plan §3ter). La traduction clé→route reste dans
 * portalRouting.js (seul responsable) ; ici : registre de validation et
 * calcul de la clé effective d'un rôle (`tenant_roles`).
 *
 * Règles §3ter appliquées :
 *  - clé explicite inconnue → null (rejet strict, jamais une clé devinée) ;
 *  - rôle issu de la migration automatique → clé calculée depuis le rôle
 *    système d'origine (SYS_CODE_TO_TEMPLATE_KEY) ;
 *  - rôle cloné → hérite la clé du rôle source (RoleFormModal) ;
 *  - rôle sans modèle résolu → activation ET affectation bloquées en mode
 *    dynamic (AffectationsManager/RolesPermissions).
 *
 * Cas M13 (arbitrage documenté, à confirmer au Lot F) : les rôles de nature
 * étudiant/enseignant/parent n'ont PAS de clé dans le registre fermé (les 20
 * clés couvrent les cockpits STAFF + génériques Fonction) mais possèdent un
 * modèle résolu par nature — leur portail M13 dédié, routé par le pont
 * existant. Ils ne sont donc pas bloqués. Idem `master` (Master Shell).
 */
import { COCKPIT_TEMPLATE_KEYS } from './portalRouting';

const TEMPLATE_KEY_SET = new Set(COCKPIT_TEMPLATE_KEYS);

/**
 * Clé calculée depuis le code d'un rôle système d'origine (rôles créés par
 * MigrationWizard : `tenant_roles.code = sysId`). Les 5 rôles CBAC v4 ont un
 * cockpit dédié homonyme ; les rôles génériques atterrissent sur le repli de
 * leur portail Fonction (la nuance par titre legacy — Doyen vs Directeur —
 * reste portée par LEGACY_ROLE_HOME_ROUTE tant que le tenant n'affine pas la
 * clé de ses rôles).
 */
export const SYS_CODE_TO_TEMPLATE_KEY = {
  dg:              'generic_piloter',
  directeur:       'generic_piloter',
  resp_academique: 'generic_produire',
  gestionnaire:    'generic_produire',
  controle:        'generic_controler',
  amelioration:    'generic_ameliorer',
  rrh:             'rrh',
  daf:             'daf',
  resp_log:        'resp_log',
  resp_com:        'resp_com',
  resp_rech:       'resp_rech',
};

/** Rôles dont le modèle est résolu PAR NATURE, hors registre staff :
 * portails M13 dédiés (+ Master Shell). Voir l'arbitrage documenté en tête. */
export const NON_TEMPLATE_RESOLVED_CODES = new Set(['etudiant', 'enseignant', 'parent', 'master']);

/**
 * Clé de modèle effective d'un rôle `tenant_roles` : la clé explicite si elle
 * est valide, sinon la clé calculée depuis le code, sinon null.
 * @param {{ cockpit_template_key?: string|null, code?: string }} role
 * @returns {string|null}
 */
export function computeCockpitTemplateKey(role) {
  if (!role) return null;
  const explicit = role.cockpit_template_key;
  if (explicit) return TEMPLATE_KEY_SET.has(explicit) ? explicit : null;
  return SYS_CODE_TO_TEMPLATE_KEY[role.code] || null;
}

/**
 * Le rôle a-t-il un modèle de cockpit résolu ? (clé de registre effective, ou
 * portail M13/Master par nature.) Faux → activation et affectation bloquées
 * en mode dynamic (§3ter — un blocage, pas un avertissement).
 */
export function hasResolvedCockpitModel(role) {
  if (!role) return false;
  if (computeCockpitTemplateKey(role) !== null) return true;
  return NON_TEMPLATE_RESOLVED_CODES.has(role.code);
}
